import classnames from 'classnames';
import React, { useState, useEffect, useContext } from 'react';
import styled, { ThemeContext, ThemeProvider } from 'styled-components';
import { InputLabelPositions } from '../../../../enums/input.js';
import { convertRgbToHsl, convertRgbToHex, convertHex3toHex6, isValidColor, convertHexToRgb, convertHexToHsl, convertHslToRgb, convertHslToHex, isHexColor, isHslColor, isRgbColor } from '../../../../utilities/color.js';
import 'date-fns';
import '../../../../utilities/locale.js';
import { useMounted } from '../../../../hooks/use-mounted.js';
import '../../../../hooks/use-prevent-keyboard-scrolling.js';
import '../../../../themes/alert.js';
import { borderRadiusLarge } from '../../../../themes/borders.js';
import '../../../../themes/button.js';
import '../../../../themes/checkbox.js';
import '../../../../themes/divider.js';
import '../../../../themes/dropdown.js';
import '../../../../themes/fieldset.js';
import '../../../../themes/input.js';
import '../../../../themes/list.js';
import '../../../../themes/listbox.js';
import '../../../../themes/loader.js';
import '../../../../themes/option.js';
import '../../../../themes/popout.js';
import '../../../../themes/switch.js';
import { lightTheme } from '../../../../themes/themes.js';
import '../../../../themes/tooltip.js';
import { StandardLabel } from '../../../atoms/label/standard-label/standard-label.js';
import { StandardTextfield } from '../../textfield/standard-textfield/standard-textfield.js';
import { BaseColorPicker } from '../base-color-picker/base-color-picker.js';

const defaultStandardColorPickerLabels = {
    bLabel: 'B',
    gLabel: 'G',
    hLabel: 'H',
    hexLabel: 'HEX',
    hslLabel: 'HSL',
    lLabel: 'L',
    rLabel: 'R',
    rgbLabel: 'RGB',
    sLabel: 'S'
};
const DEFAULT_COLORS = {
    hex: '#ffffff',
    hsl: {
        h: 0,
        s: 0,
        l: 100
    },
    rgb: {
        r: 255,
        g: 255,
        b: 255
    }
};
const BaseStandardColorPicker = (props)=>{
    const { className, defaultColor, fieldProps, fullWidth, id, labels = defaultStandardColorPickerLabels, onChange, showHex, showHsl, showRgb, textfieldProps, ...otherProps } = props;
    const { bLabel, gLabel, hLabel, hexLabel, hslLabel, lLabel, rLabel, rgbLabel, sLabel } = labels;
    const getDefaultColors = ()=>{
        if (!defaultColor || defaultColor && !isValidColor(defaultColor)) {
            return DEFAULT_COLORS;
        }
        if (isHexColor(defaultColor)) {
            return {
                hex: defaultColor,
                hsl: convertHexToHsl(defaultColor),
                rgb: convertHexToRgb(defaultColor)
            };
        }
        if (isHslColor(defaultColor)) {
            return {
                hex: convertHslToHex(defaultColor),
                hsl: defaultColor,
                rgb: convertHslToRgb(defaultColor)
            };
        }
        if (isRgbColor(defaultColor)) {
            return {
                hex: convertRgbToHex(defaultColor),
                hsl: convertRgbToHsl(defaultColor),
                rgb: defaultColor
            };
        }
        return DEFAULT_COLORS;
    };
    const defaultColors = getDefaultColors();
    const [colors, setColors] = useState(defaultColors);
    const [hex, setHex] = useState(defaultColors.hex);
    const [hsl, setHsl] = useState(defaultColors.hsl);
    const [rgb, setRgb] = useState(defaultColors.rgb);
    const isMounted = useMounted();
    useEffect(()=>{
        if (isMounted) {
            setHex(colors.hex);
            setHsl(colors.hsl);
            setRgb(colors.rgb);
            onChange(colors);
        }
    }, [
        colors
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    // eslint-disable-next-line unicorn/consistent-function-scoping
    const enforceMaxMin = (max, min, value)=>{
        /**
     * unary coerces string into a number and drops leading zeros
     * if the string isn't a number, returns NaN
     */ const v = +value;
        if (Number.isNaN(v)) {
            return min;
        }
        if (v > max) {
            return max;
        }
        if (v < min) {
            return min;
        }
        return v;
    };
    return /*#__PURE__*/ React.createElement("div", {
        ...otherProps,
        className: classnames({
            'bf-color-picker__standard': true,
            'bf-color-picker__standard--full-width': fullWidth,
            [`${className}`]: !!className
        })
    }, /*#__PURE__*/ React.createElement(BaseColorPicker, {
        className: "bf-color-picker__picker",
        rgb: colors.rgb,
        onChange: (color)=>{
            setColors({
                hex: convertRgbToHex(color),
                hsl: convertRgbToHsl(color),
                rgb: color
            });
        }
    }), showHex && /*#__PURE__*/ React.createElement("div", {
        ...fieldProps,
        className: classnames({
            'bf-color-picker__field': true,
            [`${fieldProps?.className}`]: !!fieldProps?.className
        })
    }, /*#__PURE__*/ React.createElement(StandardTextfield, {
        ...textfieldProps,
        className: "bf-color-picker__string bf-color-picker__hex",
        id: `${id}-hex`,
        label: hexLabel,
        name: `${id}-hex`,
        onBlur: (e)=>{
            const { value } = e.target;
            const newHex = convertHex3toHex6(value);
            if (newHex !== colors.hex && isValidColor(newHex)) {
                setColors({
                    hex: newHex,
                    hsl: convertHexToHsl(newHex),
                    rgb: convertHexToRgb(newHex)
                });
            } else {
                setHex(colors.hex);
            }
        },
        onChange: (e)=>{
            setHex(e.target.value);
        },
        labelPosition: InputLabelPositions.Left,
        value: hex
    })), showRgb && /*#__PURE__*/ React.createElement("div", {
        ...fieldProps,
        className: classnames({
            'bf-color-picker__field': true,
            'bf-color-picker__numbers': true,
            'bf-color-picker__rgb': true,
            [`${fieldProps?.className}`]: !!fieldProps?.className
        })
    }, /*#__PURE__*/ React.createElement(StandardLabel, {
        htmlFor: `${id}-r`
    }, rgbLabel), /*#__PURE__*/ React.createElement(StandardTextfield, {
        ...textfieldProps,
        className: "bf-color-picker__number bf-color-picker__r",
        id: `${id}-r`,
        label: rLabel,
        name: `${id}-r`,
        onBlur: (e)=>{
            const { value } = e.target;
            const r = enforceMaxMin(255, 0, value);
            const newRgb = {
                ...rgb,
                r
            };
            if (r !== colors.rgb.r && isValidColor(newRgb)) {
                setColors({
                    hex: convertRgbToHex(newRgb),
                    hsl: convertRgbToHsl(newRgb),
                    rgb: newRgb
                });
            } else {
                setRgb(colors.rgb);
            }
        },
        onChange: (e)=>{
            setRgb({
                ...rgb,
                r: Number(e.target.value)
            });
        },
        labelPosition: InputLabelPositions.Left,
        max: 255,
        min: 0,
        pattern: "[0-9]*",
        showLabel: false,
        textfieldProps: {
            inputMode: 'numeric',
            step: 1
        },
        type: "number",
        value: rgb.r
    }), /*#__PURE__*/ React.createElement(StandardTextfield, {
        ...textfieldProps,
        className: "bf-color-picker__number bf-color-picker__g",
        id: `${id}-g`,
        label: gLabel,
        name: `${id}-g`,
        onBlur: (e)=>{
            const { value } = e.target;
            const g = enforceMaxMin(255, 0, value);
            const newRgb = {
                ...rgb,
                g
            };
            if (g !== colors.rgb.g && isValidColor(newRgb)) {
                setColors({
                    hex: convertRgbToHex(newRgb),
                    hsl: convertRgbToHsl(newRgb),
                    rgb: newRgb
                });
            } else {
                setRgb(colors.rgb);
            }
        },
        onChange: (e)=>{
            setRgb({
                ...rgb,
                g: Number(e.target.value)
            });
        },
        labelPosition: InputLabelPositions.Left,
        max: 255,
        min: 0,
        pattern: "[0-9]*",
        showLabel: false,
        textfieldProps: {
            inputMode: 'numeric',
            step: 1
        },
        type: "number",
        value: rgb.g
    }), /*#__PURE__*/ React.createElement(StandardTextfield, {
        ...textfieldProps,
        className: "bf-color-picker__number bf-color-picker__b",
        id: `${id}-b`,
        label: bLabel,
        name: `${id}-b`,
        onBlur: (e)=>{
            const { value } = e.target;
            const b = enforceMaxMin(255, 0, value);
            const newRgb = {
                ...rgb,
                b
            };
            if (b !== colors.rgb.b && isValidColor(newRgb)) {
                setColors({
                    hex: convertRgbToHex(newRgb),
                    hsl: convertRgbToHsl(newRgb),
                    rgb: newRgb
                });
            } else {
                setRgb(colors.rgb);
            }
        },
        onChange: (e)=>{
            setRgb({
                ...rgb,
                b: Number(e.target.value)
            });
        },
        labelPosition: InputLabelPositions.Left,
        max: 255,
        min: 0,
        pattern: "[0-9]*",
        showLabel: false,
        textfieldProps: {
            inputMode: 'numeric',
            step: 1
        },
        type: "number",
        value: rgb.b
    })), showHsl && /*#__PURE__*/ React.createElement("div", {
        ...fieldProps,
        className: classnames({
            'bf-color-picker__field': true,
            'bf-color-picker__numbers': true,
            'bf-color-picker__hsl': true,
            [`${fieldProps?.className}`]: !!fieldProps?.className
        })
    }, /*#__PURE__*/ React.createElement(StandardLabel, {
        htmlFor: `${id}-h`
    }, hslLabel), /*#__PURE__*/ React.createElement(StandardTextfield, {
        ...textfieldProps,
        className: "bf-color-picker__number bf-color-picker__h",
        id: `${id}-h`,
        label: hLabel,
        name: `${id}-h`,
        onBlur: (e)=>{
            const { value } = e.target;
            const h = enforceMaxMin(360, 0, value);
            const newHsl = {
                ...hsl,
                h
            };
            if (h !== colors.hsl.h && isValidColor(newHsl)) {
                setColors({
                    hex: convertHslToHex(newHsl),
                    hsl: newHsl,
                    rgb: convertHslToRgb(newHsl)
                });
            } else {
                setHsl(colors.hsl);
            }
        },
        onChange: (e)=>{
            setHsl({
                ...hsl,
                h: Number(e.target.value)
            });
        },
        labelPosition: InputLabelPositions.Left,
        max: 360,
        min: 0,
        pattern: "[0-9]*",
        showLabel: false,
        textfieldProps: {
            inputMode: 'numeric',
            step: 1
        },
        type: "number",
        value: hsl.h
    }), /*#__PURE__*/ React.createElement(StandardTextfield, {
        ...textfieldProps,
        className: "bf-color-picker__number bf-color-picker__s",
        id: `${id}-s`,
        label: sLabel,
        name: `${id}-s`,
        onBlur: (e)=>{
            const { value } = e.target;
            const s = enforceMaxMin(100, 0, value);
            const newHsl = {
                ...hsl,
                s
            };
            if (s !== colors.hsl.s && isValidColor(newHsl)) {
                setColors({
                    hex: convertHslToHex(newHsl),
                    hsl: newHsl,
                    rgb: convertHslToRgb(newHsl)
                });
            } else {
                setHsl(colors.hsl);
            }
        },
        onChange: (e)=>{
            setHsl({
                ...hsl,
                s: Number(e.target.value)
            });
        },
        labelPosition: InputLabelPositions.Left,
        max: 100,
        min: 0,
        pattern: "[0-9]*",
        showLabel: false,
        textfieldProps: {
            inputMode: 'numeric',
            step: 1
        },
        type: "number",
        value: hsl.s
    }), /*#__PURE__*/ React.createElement(StandardTextfield, {
        ...textfieldProps,
        className: "bf-color-picker__number bf-color-picker__l",
        id: `${id}-l`,
        label: lLabel,
        name: `${id}-l`,
        onBlur: (e)=>{
            const { value } = e.target;
            const l = enforceMaxMin(100, 0, value);
            const newHsl = {
                ...hsl,
                l
            };
            if (l !== colors.hsl.l && isValidColor(newHsl)) {
                setColors({
                    hex: convertHslToHex(newHsl),
                    hsl: newHsl,
                    rgb: convertHslToRgb(newHsl)
                });
            } else {
                setHsl(colors.hsl);
            }
        },
        onChange: (e)=>{
            setHsl({
                ...hsl,
                l: Number(e.target.value)
            });
        },
        labelPosition: InputLabelPositions.Left,
        max: 100,
        min: 0,
        pattern: "[0-9]*",
        showLabel: false,
        textfieldProps: {
            inputMode: 'numeric',
            step: 1
        },
        type: "number",
        value: hsl.l
    })));
};
const ColorPicker = styled(BaseStandardColorPicker)`
  ${({ fullWidth })=>fullWidth ? 'width: 100%;' : 'width: 220px;'};

  .react-colorful {
    height: 150px;
    width: 100%;

    .react-colorful__saturation {
      border-top-left-radius: ${borderRadiusLarge}px;
      border-top-right-radius: ${borderRadiusLarge}px;
    }

    .react-colorful__last-control {
      border-bottom-left-radius: ${borderRadiusLarge}px;
      border-bottom-right-radius: ${borderRadiusLarge}px;
    }
  }

  .bf-textfield__textfield {
    height: 35px;
    min-height: 0;
    padding: 6px 7px;
  }

  .bf-color-picker__field {
    margin-top: 12px;
  }

  .bf-color-picker__hex {
    .bf-textfield__header {
      margin-inline-end: 7px;
    }
  }

  .bf-color-picker__numbers {
    align-items: center;
    display: flex;

    .bf-textfield__header {
      margin: 0;
    }

    > .bf-label__standard {
      margin-inline-end: 7px;
    }
  }

  .bf-color-picker__number {
    width: 100px;
    margin-inline-end: 5px;

    &:last-child {
      margin-inline-end: 0;
    }
  }
`;
/**
 * A color picker component that supports
 * [HEX](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color),
 * [RGB](https://developer.mozilla.org/en-US/docs/Web/CSS/color_value/rgb),
 * and [HSL](https://developer.mozilla.org/en-US/docs/Web/CSS/color_value/hsl) CSS colors.
 *
 * Required fields include `id` and `onChange`. `onChange` always returns an object containing
 * a HEX string, RGB object, and HSL object (conversion is handle within the component).
 *
 * You may optionally pass in a `defaultColor` that is a valid HEX string, RGB object, or
 * HSL object which will display as the initial color shown in the picker. If no `defaultColor`
 * is passed, the color picker will show `white` as the starting color. You can also hide
 * inputs by setting `showHex`, `showRgb`, or `showHsl` to `false`.
 *
 * This component is unique in that it offers default labels since the translation service
 * that Brandfolder uses doesn't translate HEX, RGB, or HSL abbreviations and acronyms.
 * You may still pass in a `labels` object, if needed.
 *
 * In order to use this component, you will need to have
 * [react-colorful](https://omgovich.github.io/react-colorful/) and
 * [colord](https://github.com/omgovich/colord) packages installed as peer dependencies within you app.
 *
 * @param props StandardColorPickerProps
 * @returns FunctionComponent<StandardColorPickerProps>
 */ const StandardColorPicker = (props)=>{
    const { fullWidth = false, showHex = true, showHsl = true, showRgb = true } = props;
    const themeContext = useContext(ThemeContext);
    return /*#__PURE__*/ React.createElement(ThemeProvider, {
        theme: themeContext || lightTheme
    }, /*#__PURE__*/ React.createElement(ColorPicker, {
        ...props,
        fullWidth: fullWidth,
        showHex: showHex,
        showHsl: showHsl,
        showRgb: showRgb
    }));
};

export { BaseStandardColorPicker, StandardColorPicker, defaultStandardColorPickerLabels };
//# sourceMappingURL=standard-color-picker.js.map
